import React, { useState, useEffect } from "react";
import styled from "styled-components";

/* ================= BRAND ================= */

const BRAND_GRADIENT = "linear-gradient(135deg, #5f2eea, #0a84ff)";
const BASE_URL = "http://165.22.218.70:1112";

/* ================= WRAPPER ================= */

const Wrapper = styled.div`
  font-family: "Poppins", sans-serif;
  min-height: 100vh;
  padding: 40px 20px;
  color: #fff;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px);
  border-radius: 20px;
  padding: 35px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 30px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 35px;
  align-items: flex-end;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.85rem;
  margin-bottom: 6px;
  color: #cbd5e1;
`;

const Input = styled.input`
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.95rem;
  width: 240px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;

  &:focus {
    outline: none;
    border-color: #0a84ff;
  }
`;

const Button = styled.button`
  background: ${(props) =>
    props.variant === "in"
      ? "linear-gradient(135deg, #28a745, #20c997)"
      : "linear-gradient(135deg, #dc3545, #ff6b6b)"};

  color: #fff;
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  height: 46px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LeaveWarning = styled.p`
  color: #ff6b6b;
  font-weight: 600;
  margin-top: -15px;
  margin-bottom: 15px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 14px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 650px;
`;

const Thead = styled.thead`
  background: ${BRAND_GRADIENT};
  color: #fff;
`;

const Th = styled.th`
  padding: 14px;
  text-align: left;
  font-size: 0.9rem;
`;

const Tr = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 14px;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span`
  background: ${(props) =>
    props.present ? "rgba(40,167,69,0.2)" : "rgba(220,53,69,0.2)"};
  color: ${(props) => (props.present ? "#28a745" : "#dc3545")};
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #94a3b8;
`;

/* ================= COMPONENT ================= */

export default function Attendance() {
  const [userId, setUserId] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOnLeaveToday, setIsOnLeaveToday] = useState(false);

  /* ================= CHECK LEAVE ================= */

  const checkLeaveStatus = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/webapp/leave/user/${id}`);
      if (!res.ok) return;

      const leaves = await res.json();
      const today = new Date().toISOString().split("T")[0];

      const onLeave = leaves.some(
        (leave) =>
          leave.status === "Approved" &&
          today >= leave.startDate &&
          today <= leave.endDate
      );

      setIsOnLeaveToday(onLeave);
    } catch {
      setIsOnLeaveToday(false);
    }
  };

  /* ================= FETCH ATTENDANCE ================= */

  const fetchAttendance = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/attendance/user/${id}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRecords(data || []);
    } catch {
      setRecords([]);
    }
  };

  /* ================= PUNCH IN ================= */

  const punchIn = async () => {
    if (!userId) return alert("Enter User ID");
    if (isOnLeaveToday)
      return alert("You are on approved leave today.");

    setLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/webapp/attendance/punch-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: Number(userId) }),
      });

      if (!res.ok) throw new Error();
      await fetchAttendance(userId);
    } catch {
      alert("Punch In failed");
    }

    setLoading(false);
  };

  /* ================= PUNCH OUT ================= */

  const punchOut = async () => {
    if (!userId) return alert("Enter User ID");
    if (isOnLeaveToday)
      return alert("You are on approved leave today.");

    setLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/webapp/attendance/punch-in`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: Number(userId) }),
      });

      if (!res.ok) throw new Error();
      await fetchAttendance(userId);
    } catch {
      alert("Punch Out failed");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (userId) {
      fetchAttendance(userId);
      checkLeaveStatus(userId);
    }
  }, [userId]);

  return (
    <Wrapper>
      <Card>
        <Title>Employee Attendance</Title>

        <Form onSubmit={(e) => e.preventDefault()}>
          <InputGroup>
            <Label>User ID</Label>
            <Input
              type="number"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
            />
          </InputGroup>

          <Button
            variant="in"
            type="button"
            onClick={punchIn}
            disabled={loading || isOnLeaveToday}
          >
            {loading ? "Processing..." : "Punch In"}
          </Button>

          <Button
            variant="out"
            type="button"
            onClick={punchOut}
            disabled={loading || isOnLeaveToday}
          >
            {loading ? "Processing..." : "Punch Out"}
          </Button>
        </Form>

        {isOnLeaveToday && (
          <LeaveWarning>
            You are on approved leave today. Attendance marking is disabled.
          </LeaveWarning>
        )}

        <TableWrapper>
          {records.length === 0 ? (
            <EmptyState>No attendance records found.</EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>User</Th>
                  <Th>Date</Th>
                  <Th>Present</Th>
                  <Th>Time In</Th>
                  <Th>Time Out</Th>
                  <Th>Work Hours</Th>
                </tr>
              </Thead>
              <tbody>
                {records.map((record) => (
                  <Tr key={record.id}>
                    <Td>{record.user?.name}</Td>
                    <Td>{record.date}</Td>
                    <Td>
                      <StatusBadge present={record.present}>
                        {record.present ? "Yes" : "No"}
                      </StatusBadge>
                    </Td>
                    <Td>{record.timeIn || "-"}</Td>
                    <Td>{record.timeOut || "-"}</Td>
                    <Td>{record.workHours || "-"}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableWrapper>
      </Card>
    </Wrapper>
  );
}